import axios, { type AxiosRequestConfig } from 'axios';

import { ERROR, FETCHING_COMPLETE, IS_FETCHING } from '../actions/appState';
import { getIdToken, userLogOut } from '../utils/AuthUtil';

const BASE_URL = `${process.env.API_URL}/`;

function callApi(
  endpoint: string,
  method: string,
  params: Record<string, any>,
  body: any,
  headers: Record<string, any>,
  authenticated: boolean,
  abortSignal?: AbortSignal
) {
  const token = getIdToken() || null;
  const mandatoryHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Pragma: 'no-cache',
  };

  const requestHeaders = Object.assign({}, mandatoryHeaders, headers);
  const config: AxiosRequestConfig = {
    method,
    params,
    headers: requestHeaders,
    data: body,
    url: BASE_URL + endpoint,
    ...(abortSignal && { signal: abortSignal }),
  };

  if (authenticated) {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      throw new Error('No token saved!');
    }
  }

  return axios
    .request(config)
    .then((response: any) => {
      // TODO
      // Get the expected response status from the action.
      if (response.status !== 200 && response.status !== 201) {
        throw Error(response.statusText);
      }

      return response.data;
    })
    .catch((error) => {
      if (error.response?.status === 401) {
        userLogOut(true);

        return;
      }
      if (!axios.isCancel(error)) {
        throw error;
      }
    });
}

export const CALL_API = Symbol('Call API');

export default (store) => (next) => (action) => {
  const callAPI = action[CALL_API];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  const {
    endpoint,
    method,
    params,
    body,
    headers,
    types,
    authenticated,
    hideLoadingWidget,
    errorDismissible,
    hideDefaultAlert,
    uniqueIdentifier,
    abortSignal,
    agencyId,
  } = callAPI;

  // Use the ES6 format while implementing retries.
  const [requestType, successType, errorType] = types;
  // const successType = types[1];
  // const errorType = types[2];

  store.dispatch({ type: IS_FETCHING, hideLoadingWidget });
  store.dispatch({
    type: requestType,
    hideLoadingWidget,
    ...(uniqueIdentifier ? { uniqueIdentifier } : {}),
    ...(agencyId != null ? { agencyId } : {}),
  });

  /* Passing the authenticated boolean back in our data will let us
   * distinguish between normal and secret quotes
   */
  return callApi(endpoint, method, params, body, headers, authenticated, abortSignal).then(
    (response) => {
      store.dispatch({ type: FETCHING_COMPLETE, hideLoadingWidget });

      return next({
        response,
        authenticated,
        type: successType,
        uniqueIdentifier,
        ...(agencyId != null ? { agencyId } : {}),
      });
    },
    (error) => {
      if (!hideDefaultAlert) {
        store.dispatch({
          type: ERROR,
          errorMessage: error.response ? error.response.data.message : error.message,
          errorDismissible,
          hideLoadingWidget,
        });
      }

      const data = error.response?.data;
      const previewMetadata: { modelVersion?: string; promptVersion?: string; targetLanguage?: string } = {};
      // Allow-list. A preview failure may carry model metadata; it must not carry message text.
      if (typeof data?.modelVersion === 'string') {
        previewMetadata.modelVersion = data.modelVersion;
      }
      if (typeof data?.promptVersion === 'string') {
        previewMetadata.promptVersion = data.promptVersion;
      }
      if (typeof data?.targetLanguage === 'string') {
        previewMetadata.targetLanguage = data.targetLanguage;
      }

      return next({
        error: {
          message: data?.message || error?.message || 'There was an error.',
          // Machine-readable discriminator when the endpoint defines one, so callers can branch
          // on it instead of matching on the message.
          code: data?.code,
          originalMessage: error?.message,
          serverMessage: data?.message,
          ...previewMetadata,
        },
        type: errorType,
        originalError: error,
        uniqueIdentifier,
        ...(agencyId != null ? { agencyId } : {}),
      });
    }
  );
};
